import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    Button,
    Box,
    Typography,
    IconButton,
    Snackbar,
    Alert,
    CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CloseIcon from '@mui/icons-material/Close';
import { handleRegister as registerForEvent, checkEnrollmentStatus } from '../service/home';
import { getCurrentUser } from '../utils/auth';

const EventModal = ({ open, onClose, eventType, eventTitle, eventIcon, showVideo, isRegistered = false, onRegister = () => { }, eventData, currentUser, setCurrentUser }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentSourceIndex, setCurrentSourceIndex] = useState(0);
    const [isEnrolled, setIsEnrolled] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isRegistering, setIsRegistering] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [retryCount, setRetryCount] = useState(0);

    const videoRef = React.useRef(null);

    // Get current user using auth utility (make it reactive)
    // const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const user = getCurrentUser() || { id: null };
        setCurrentUser(user);
        // Determine actual event type (handle race condition where eventType might be null initially)
        const actualEventType = eventType || (eventTitle?.includes('Webinar') ? 'webinar' : eventType);

        // Check if user is already enrolled based on webinar_status
        const hasUser = !!user;
        const hasWebinarStatus = user?.webinarStatus === 'registered';
        const isWebinarEvent = actualEventType === 'webinar';
        const shouldSetEnrolled = hasUser && hasWebinarStatus && isWebinarEvent;

        if (shouldSetEnrolled) {
            setIsEnrolled(true);
            setIsLoading(false);
        }
    }, [open, eventType, eventTitle]); // Refresh when modal opens

    const videoSources = [
        { src: '/videos/morning-yoga-hd.mp4', type: 'video/mp4', quality: '1080p' },
        { src: '/videos/morning-yoga-sd.mp4', type: 'video/mp4', quality: '720p' },
        { src: '/videos/morning-yoga-low.mp4', type: 'video/mp4', quality: '480p' }
    ];

    // Enrollment check on modal open
    useEffect(() => {
        const checkEnrollment = async () => {
            try {
                if (!currentUser || !currentUser.id) {
                    console.error('No user ID found');
                    setIsEnrolled(false);
                    setIsLoading(false);
                    return;
                }

                // Determine actual event type (handle race condition)
                const actualEventType = eventType || (eventTitle?.includes('Webinar') ? 'webinar' : eventType);

                // If user already has webinar_status = 'registered', skip API call
                if (currentUser.webinarStatus === 'registered' && actualEventType === 'webinar') {
                    console.log('User already enrolled based on webinar_status');
                    setIsEnrolled(true);
                    setIsLoading(false);
                    return;
                }

                // Otherwise, check via API
                const result = await checkEnrollmentStatus(actualEventType, currentUser.id);
                setIsEnrolled(result.isEnrolled);
            } catch (error) {
                console.error(error);
                setIsEnrolled(false);
            } finally {
                setIsLoading(false);
            }
        };
        if (open && currentUser) checkEnrollment();
    }, [open, eventType, eventTitle, currentUser]);

    useEffect(() => {
        if (!open) {
            setIsEnrolled(false);
            setIsLoading(true);
        }
    }, [open]);

    const handleRegister = async () => {
        if (isEnrolled) return;

        // Validate user ID
        if (!currentUser || !currentUser.id) {
            setSnackbar({ open: true, message: 'Please log in to register for events', severity: 'error' });
            return;
        }

        setIsRegistering(true);

        try {
            console.log('handleRegister - currentUser object:', currentUser); // Debug log
            console.log('handleRegister - currentUser.id:', currentUser.id); // Debug log

            const eventData = {
                eventType: eventType,
                eventTitle: eventTitle,
                userId: currentUser.id
            };

            console.log('handleRegister - eventData being sent:', eventData); // Debug log

            const result = await registerForEvent(eventData);

            if (result.success) {
                setIsEnrolled(true);

                // ✅ Update localStorage webinar_status to "registered"
                const storedUser = JSON.parse(localStorage.getItem('user')) || {};
                storedUser.webinar_status = 'registered';
                localStorage.setItem('user', JSON.stringify(storedUser));

                setCurrentUser(getCurrentUser());

                if (result.isAlreadyEnrolled) {
                    setSnackbar({ open: true, message: result.message, severity: 'info' });
                } else {
                    setSnackbar({ open: true, message: result.message, severity: 'success' });
                }
            } else {
                setSnackbar({ open: true, message: result.error, severity: 'error' });
            }
        } catch (error) {
            console.error('Registration error:', error);
            setSnackbar({ open: true, message: 'An unexpected error occurred', severity: 'error' });
        } finally {
            setIsRegistering(false);
        }
    };

    const checkVideoExists = async (url) => {
        try {
            const response = await fetch(url, { method: 'HEAD' });
            return response.ok;
        } catch {
            return false;
        }
    };

    const loadVideo = async () => {
        if (!videoRef.current) return;
        setIsLoading(true); setIsPlaying(false);
        try {
            const currentSource = videoSources[currentSourceIndex];
            const exists = await checkVideoExists(currentSource.src);
            if (!exists) { await tryNextSource(); return; }
            videoRef.current.src = currentSource.src;
            videoRef.current.type = currentSource.type;
            videoRef.current.load();

            const loadTimeout = setTimeout(() => {
                if (videoRef.current && videoRef.current.readyState < 2) tryNextSource();
            }, 5000);

            const onCanPlay = () => {
                clearTimeout(loadTimeout);
                videoRef.current.removeEventListener('canplay', onCanPlay);
                setIsLoading(false); setRetryCount(0);
                if (isPlaying) videoRef.current.play().catch(() => setIsPlaying(false));
            };
            videoRef.current.addEventListener('canplay', onCanPlay);

            return () => clearTimeout(loadTimeout);
        } catch {
            setIsLoading(false);
        }
    };

    const tryNextSource = async () => {
        if (currentSourceIndex < videoSources.length - 1) {
            const nextIndex = currentSourceIndex + 1;
            const backoffTime = Math.min(1000 * Math.pow(2, retryCount), 10000);
            setRetryCount(prev => prev + 1);
            await new Promise(resolve => setTimeout(resolve, backoffTime));
            setCurrentSourceIndex(nextIndex);
            setTimeout(loadVideo, 100);
        } else {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!open) { if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; videoRef.current.load(); } setIsPlaying(false); return; }
        setCurrentSourceIndex(0); setRetryCount(0); setIsLoading(true); setIsPlaying(false);
        loadVideo();
    }, [open, currentSourceIndex]);

    // Default modal for other event types (webinar/therapy)
    return (
        <Dialog open={open} onClose={onClose} maxWidth={showVideo ? 'md' : 'sm'} fullWidth PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(16, 54, 128, 0.15)', maxWidth: showVideo ? '800px' : '500px', width: '100%', overflow: 'hidden', backgroundColor: showVideo ? '#000' : 'white', color: showVideo ? 'white' : 'inherit' } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: showVideo ? '#000' : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', color: showVideo ? 'white' : '#0f172a', p: 2, borderBottom: showVideo ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {eventIcon && <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: showVideo ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: showVideo ? '0 2px 12px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.1)' }}>{React.cloneElement(eventIcon, { sx: { color: showVideo ? '#fff' : '#fff', fontSize: '1.1rem' } })}</Box>}
                    <Box>
                        <Typography variant="subtitle1" sx={{ color: showVideo ? 'white' : '#0f172a', fontWeight: 600, fontSize: '1rem', lineHeight: 1.3 }}>{eventTitle || 'Event Registration'}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: showVideo ? 'white' : '#64748b', backgroundColor: showVideo ? 'rgba(255,255,255,0.1)' : 'rgba(100,116,139,0.1)', '&:hover': { backgroundColor: showVideo ? 'rgba(255,255,255,0.2)' : 'rgba(100,116,139,0.2)' }, width: 32, height: 32, transition: 'all 0.2s ease', '&:active': { transform: 'scale(0.95)' } }}><CloseIcon sx={{ fontSize: '1rem' }} /></IconButton>
            </DialogTitle>

            {/* Enrollment / Registration content */}
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}><CircularProgress /></Box>
            ) : isEnrolled ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 4, px: 3, background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderRadius: 2, m: 2 }}>
                    <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 3, color: '#059669' }} />
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1, color: '#065f46' }}>Already Enrolled!</Typography>
                    <Typography variant="body1" sx={{ mb: 4, maxWidth: '85%', color: '#047857', lineHeight: 1.6 }}>
                        Great news! You're already registered for this {eventType === 'webinar' ? 'webinar' : 'event'}.
                        We'll send you details and reminders as the date approaches.
                    </Typography>
                    <Button
                        onClick={onClose}
                        variant="contained"
                        sx={{
                            minWidth: '180px',
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                            backgroundColor: '#059669',
                            '&:hover': { backgroundColor: '#047857' },
                            boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)'
                        }}
                    >
                        Got it!
                    </Button>
                </Box>
            ) : (
                <Box sx={{ width: '100%', textAlign: 'center', p: 3, borderRadius: 2, backgroundColor: 'rgba(249,250,251,0.9)', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', border: '1px solid #E5E7EB' }}>
                    <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: 'rgba(79,70,229,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}><EventNoteIcon color="primary" sx={{ fontSize: 32 }} /></Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>Confirm Your Registration</Typography>
                    <Typography variant="body1" sx={{ color: '#4B5563', mb: 4, lineHeight: 1.6 }}>Are you sure you want to register for <strong>{eventTitle || 'this webinar'}</strong>? Click confirm to secure your spot!</Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', pt: 1 }}>
                        <Button onClick={onClose} variant="outlined" sx={{ px: 3, py: 1.2, borderRadius: 2, textTransform: 'none', fontSize: '0.95rem', fontWeight: 500, color: '#6B7280', borderColor: '#E5E7EB', '&:hover': { borderColor: '#D1D5DB', backgroundColor: 'rgba(0,0,0,0.01)' } }}>Cancel</Button>
                        <Button onClick={handleRegister} variant="contained" disabled={isRegistering} sx={{ px: 3, py: 1.2, borderRadius: 2, textTransform: 'none', fontSize: '0.95rem', fontWeight: 600, background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)', color: 'white', boxShadow: '0 4px 6px -1px rgba(79,70,229,0.3)', '&:hover': { background: 'linear-gradient(135deg, #4338CA 0%, #6D28D9 100%)', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }, '&.Mui-disabled': { background: '#E5E7EB', color: '#9CA3AF' } }}>{isRegistering ? <><CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />Registering...</> : 'Confirm Registration'}</Button>
                    </Box>
                </Box>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Dialog>
    );
};

export default EventModal;
